import React from 'react';
import { Upload, Plus, ShoppingCart, Camera, X, ExternalLink } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

const UploadPrescription = ({
  uploadedFile, uploadPatientName, setUploadPatientName, uploadNotes, setUploadNotes,
  uploadLoading, handleUploadPrescription, handleFileUpload,
  showCamera, startCamera, stopCamera, capturePhoto, retakePhoto,
  videoRef, canvasRef, capturedImage,
  showManualEntry, setShowManualEntry, manualMedicines,
  addManualMedicine, updateManualMedicine, removeManualMedicine,
  generateLinksFromManualEntry, loading,
  uploadedPrescriptions, setSelectedUploadedPrescription, fetchMedicineLinks
}) => (
  <div className="space-y-6">
    <Card className="p-6">
      <h2 className="text-2xl font-bold text-blue-600 mb-4 flex items-center gap-2">
        <Upload className="w-6 h-6" />Upload Prescription
      </h2>
      <p className="text-gray-600 mb-6">Upload your existing prescription and we'll extract the medicines using AI for easy ordering.</p>
      <div className="space-y-4">
        <div>
          <Label htmlFor="patientNameUpload">Patient Name *</Label>
          <Input id="patientNameUpload" value={uploadPatientName} onChange={(e) => setUploadPatientName(e.target.value)} placeholder="Enter patient name" />
        </div>
        <div>
          <Label htmlFor="prescriptionFile">Prescription File (JPG, PNG, PDF) *</Label>
          {showCamera && (
            <div className="mb-4 relative">
              <video ref={videoRef} autoPlay playsInline className="w-full max-h-96 bg-black rounded-lg" />
              <div className="flex gap-2 mt-2">
                <Button type="button" onClick={capturePhoto} className="flex-1 bg-green-600 hover:bg-green-700"><Camera className="w-4 h-4 mr-2" />Capture Photo</Button>
                <Button type="button" onClick={stopCamera} variant="outline"><X className="w-4 h-4 mr-2" />Cancel</Button>
              </div>
              <canvas ref={canvasRef} style={{ display: 'none' }} />
            </div>
          )}
          {capturedImage && !showCamera && (
            <div className="mb-4">
              <img src={capturedImage} alt="Captured prescription" className="w-full max-h-96 object-contain border rounded-lg" />
              <div className="flex gap-2 mt-2">
                <Button type="button" onClick={retakePhoto} variant="outline" className="flex-1"><Camera className="w-4 h-4 mr-2" />Retake Photo</Button>
              </div>
            </div>
          )}
          {!showCamera && !capturedImage && (
            <div className="space-y-3">
              <input id="prescriptionFile" type="file" accept=".jpg,.jpeg,.png,.pdf" onChange={handleFileUpload} className="w-full px-3 py-2 border rounded-md" />
              <div className="flex items-center gap-2"><div className="flex-1 border-t border-gray-300"></div><span className="text-sm text-gray-500">OR</span><div className="flex-1 border-t border-gray-300"></div></div>
              <Button type="button" onClick={startCamera} variant="outline" className="w-full"><Camera className="w-4 h-4 mr-2" />Use Camera to Capture</Button>
            </div>
          )}
          {uploadedFile && !showCamera && !capturedImage && (
            <p className="text-sm text-green-600 mt-2">Selected: {uploadedFile.name}</p>
          )}
        </div>
        <div>
          <Label htmlFor="uploadNotes">Notes (Optional)</Label>
          <textarea id="uploadNotes" value={uploadNotes} onChange={(e) => setUploadNotes(e.target.value)} placeholder="Any additional notes about this prescription" className="w-full px-3 py-2 border rounded-md" rows="3" />
        </div>
        <Button onClick={handleUploadPrescription} disabled={uploadLoading || !uploadedFile || !uploadPatientName.trim()} className="w-full bg-blue-600 hover:bg-blue-700" data-testid="upload-prescription-btn">
          {uploadLoading ? <span className="flex items-center justify-center"><div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>Uploading & Extracting...</span> : <span className="flex items-center justify-center"><Upload className="w-5 h-5 mr-2" />Upload & Extract Medicines</span>}
        </Button>
      </div>
    </Card>

    {/* Manual Medicine Entry */}
    <Card className="p-6 bg-blue-50 border-blue-200">
      <h3 className="text-xl font-bold text-blue-800 mb-4">Enter Medicines Manually</h3>
      <p className="text-sm text-gray-700 mb-4"><strong>Note:</strong> Our AI automatically extracts medicines from uploaded prescriptions. If the automatic extraction doesn't work perfectly, you can enter your medicines manually here.</p>
      {!showManualEntry ? (
        <Button onClick={() => setShowManualEntry(true)} className="bg-blue-600 hover:bg-blue-700" data-testid="manual-entry-btn"><Plus className="w-4 h-4 mr-2" />Enter Medicines Manually</Button>
      ) : (
        <div className="space-y-4">
          {manualMedicines.map((medicine, idx) => (
            <div key={`manual-${idx}`} className="bg-white p-4 rounded-lg border space-y-3">
              <div className="flex items-center justify-between mb-2">
                <span className="font-semibold text-gray-700">Medicine {idx + 1}</span>
                {manualMedicines.length > 1 && <Button onClick={() => removeManualMedicine(idx)} size="sm" variant="outline" className="text-red-600">Remove</Button>}
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div><Label>Medicine Name *</Label><Input value={medicine.name} onChange={(e) => updateManualMedicine(idx, 'name', e.target.value)} placeholder="e.g., Paracetamol, Dolo 650" /></div>
                <div><Label>Dosage</Label><Input value={medicine.dosage} onChange={(e) => updateManualMedicine(idx, 'dosage', e.target.value)} placeholder="e.g., 500mg, 1 tablet" /></div>
                <div><Label>Frequency</Label><Input value={medicine.frequency} onChange={(e) => updateManualMedicine(idx, 'frequency', e.target.value)} placeholder="e.g., 2x daily, BD, TDS" /></div>
                <div><Label>Duration</Label><Input value={medicine.duration} onChange={(e) => updateManualMedicine(idx, 'duration', e.target.value)} placeholder="e.g., 7 days, 1 week" /></div>
              </div>
            </div>
          ))}
          <div className="flex gap-3">
            <Button onClick={addManualMedicine} variant="outline" className="flex-1"><Plus className="w-4 h-4 mr-2" />Add Another Medicine</Button>
            <Button onClick={generateLinksFromManualEntry} disabled={loading || !manualMedicines.some(m => m.name.trim())} className="flex-1 bg-green-600 hover:bg-green-700">
              {loading ? <span className="flex items-center"><div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />Generating...</span> : <span className="flex items-center"><ShoppingCart className="w-4 h-4 mr-2" />Generate Order Links</span>}
            </Button>
          </div>
        </div>
      )}
    </Card>

    {/* Previously Uploaded */}
    {uploadedPrescriptions.length > 0 && (
      <Card className="p-6">
        <h3 className="text-xl font-bold text-gray-800 mb-4">Previously Uploaded Prescriptions</h3>
        <div className="space-y-3">
          {uploadedPrescriptions.map((prescription, idx) => (
            <div key={`upload-${idx}`} className="border rounded-lg p-4 hover:bg-gray-50">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <h4 className="font-bold text-gray-800">{prescription.patient_name}</h4>
                  <p className="text-sm text-gray-600">Uploaded: {new Date(prescription.uploaded_at).toLocaleDateString()}</p>
                  <p className="text-sm text-gray-600">Medicines: {prescription.extracted_medicines?.length || 0}</p>
                </div>
                <div className="flex gap-2">
                  <Button onClick={() => setSelectedUploadedPrescription(prescription)} size="sm" variant="outline">View</Button>
                  <Button onClick={() => fetchMedicineLinks(prescription.id, 'uploaded')} size="sm" className="bg-blue-600 hover:bg-blue-700"><ShoppingCart className="w-4 h-4 mr-1" />Order</Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </Card>
    )}
  </div>
);

export default UploadPrescription;
